import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Input,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';
import InsuranceConfigOptions, { useStyles, MenuProps } from './InsuranceConfigOptions';
import { useTranslation } from 'react-i18next';
import {
  CalculationDomainEnum,
  CalculationInput,
  CalculationResult,
  CreditTypeEnum,
  LeasingTypeEnum,
} from '../../api/graphql/generated/schema';
import { CreditGroupDto, CreditGroupItemDto, LeasingGroupDto, LeasingGroupItemDto } from '../../modelView';
import { CalculationConfigDto } from '../../modelView/finance/calculation/calculationConfig';
import { useCalculateVehicleFinanceLazyQuery, useGetFinanceProductsQuery } from '../../api/graphql/generated/hooks';
import { setSnackbar, SnackbarStates } from '../../redux/snackbar';
import { useDispatch, useSelector } from 'react-redux';
import { setLoadingStatus } from '../../redux/loading';
import { CardContext, InsuranceConfig, InsuranceInput } from '../../modelView/finance/calculation/interfaces';
import { isValidCalculatedPayment } from '../../modelView/finance/instalmentValidation';
import { getGQLErrorMessages } from '../../utils/graphGL/graphQLHelper';
import { addProductDisclaimer } from '../../redux/productDisclaimers';
import { environment } from '../../environments/environment';

export default function InsuranceDetails(props: {
  cardContext: CardContext;
  constructItemDto: (calculations: CalculationResult) => CreditGroupItemDto | LeasingGroupItemDto;
  domain: CalculationDomainEnum;
  financeGroup: CreditGroupDto | LeasingGroupDto;
  handleUpdate: (
    groupItems: Array<CreditGroupItemDto> | Array<LeasingGroupItemDto>,
    calculationParams: CalculationConfigDto,
  ) => void;
  insuranceConfigOptions: InsuranceConfig;
  productDisclaimer?: string;
  financeGroupCalculationParams?: CalculationConfigDto;
  enableCreditOptions?: boolean;
}) {
  const { enableCreditOptions = false } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedProductId, setSelectedProductId] = useState<string>('');

  // get the current disclaimer from redux
  const currentDisclaimer = useSelector((state: any) => state.productDisclaimers?.disclaimers?.[selectedProductId]);

  // State for insurance mapping
  const [selectedInsuranceConfig, setSelectedInsuranceConfig] = useState<InsuranceInput | undefined>(undefined);

  const [calculationDiscount, setCalculationDiscount] = useState<number>(0);

  // State for final price of the vehicle without discount
  const [totalModelPrice, setTotalModelPrice] = useState<number>(props.cardContext.totalModelPrice);
  const debounceTimer = React.useRef<NodeJS.Timeout | null>(null);

  // State for user input for automatic recalculation flag
  const [repeatedCalculation, setRepeatedCalculation] = useState<boolean>(false);

  const [gqlCalculateFinances, { error: errorCalculationInfo, loading: loadingCalculationInfo }] =
    useCalculateVehicleFinanceLazyQuery({
      fetchPolicy: 'no-cache',
    });

  const classes = useStyles();

  const { data: financeProducts, loading: loadingFinanceProducts } = useGetFinanceProductsQuery({
    skip: !props.cardContext.modelKey || props.cardContext.modelYear == null,
    variables: {
      input: {
        domain: props.domain,
        vehicleType: props.cardContext.vehicleType,
        modelKey: (props.cardContext.modelKey ?? '').split('/')[0],
        // Skipped (above) when modelYear is null, so this fallback is never actually sent.
        modelYear: props.cardContext.modelYear ?? 0,
        regDateFirst:
          props.cardContext.regDateFirst ||
          (props.cardContext.modelYear ? new Date(props.cardContext.modelYear, 0, 1).toISOString() : undefined),
      },
    },
  });

  useEffect(() => {
    initConfig();
  }, [props.financeGroupCalculationParams]);

  useEffect(() => {
    if (errorCalculationInfo) {
      dispatch(setSnackbar(true, SnackbarStates.ERROR, getGQLErrorMessages(errorCalculationInfo)));
      reset();
    }
  }, [errorCalculationInfo]);

  useEffect(() => {
    dispatch(setLoadingStatus(loadingCalculationInfo || loadingFinanceProducts));
  }, [loadingCalculationInfo, loadingFinanceProducts, dispatch]);

  useEffect(() => {
    if (selectedProductId && props.productDisclaimer) {
      dispatch(addProductDisclaimer({ productId: selectedProductId, productDisclaimer: props.productDisclaimer }));
    }
  }, [selectedProductId, props.productDisclaimer, dispatch]);

  const initConfig = () => {
    const { financeGroupCalculationParams: params } = props;
    setSelectedInsuranceConfig({
      id: params?.insuranceId,
      packageId: params?.insurancePackageId,
      deductible: params?.insuranceDeductible,
      windshieldLimit: params?.insuranceWindshieldLimit,
      claimFreeMonths: params?.claimFreeMonths,
      clientAge: params?.clientAge,
    });
    setRepeatedCalculation(params?.automaticCalculation || false);
    setCalculationDiscount(params?.carPriceDiscount || 0);
    setTotalModelPrice(params?.totalModelPrice || props.cardContext.totalModelPrice);
    setSelectedProductId(params?.productId || '');
  };

  const updatePrice = (discountInput: number) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    setCalculationDiscount(discountInput);
    debounceTimer.current = setTimeout(() => {
      const input = constructConfigObject(
        repeatedCalculation,
        discountInput,
        totalModelPrice,
        selectedInsuranceConfig,
        selectedProductId,
      );
      const calculationParams = CalculationConfigDto.convertFromCalculationInput(input);
      props.handleUpdate(props.financeGroup.items, calculationParams);
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    }, 300);
  };

  const handleProductChange = (productId: string) => {
    const input = constructConfigObject(
      repeatedCalculation,
      calculationDiscount,
      totalModelPrice,
      selectedInsuranceConfig,
      productId,
    );
    const calculationParams = CalculationConfigDto.convertFromCalculationInput(input);
    props.handleUpdate(props.financeGroup.items, calculationParams);
  };

  const handleInsuranceConfigUpdate = (config: InsuranceInput) => {
    setSelectedInsuranceConfig(config);
    const input = constructConfigObject(
      repeatedCalculation,
      calculationDiscount,
      totalModelPrice,
      config,
      selectedProductId,
    );
    const calculationParams = CalculationConfigDto.convertFromCalculationInput(input);
    props.handleUpdate(props.financeGroup.items, calculationParams);
  };

  const inputIsValid = () => {
    return (
      Boolean(selectedInsuranceConfig) &&
      Boolean(selectedInsuranceConfig?.id) &&
      Boolean(selectedInsuranceConfig?.deductible) &&
      Boolean(selectedInsuranceConfig?.packageId) &&
      Boolean(selectedInsuranceConfig?.windshieldLimit)
    );
  };

  const calculateFinances = async (
    items: Array<CreditGroupItemDto> | Array<LeasingGroupItemDto>,
  ): Promise<Array<CalculationResult> | null> => {
    const { modelKey: cardModelKey, modelYear } = props.cardContext;
    if (!selectedInsuranceConfig || !cardModelKey || modelYear == null) {
      return null;
    }
    const [modelKey] = cardModelKey.split('/');

    const inputVars: CalculationInput = {
      productId: selectedProductId || undefined,
      domain: props.domain,
      vehicleType: props.cardContext.vehicleType,
      modelKey,
      modelYear,
      regDateFirst: props.cardContext.regDateFirst,
      totalModelPrice: totalModelPrice - calculationDiscount,
      originalModelPrice: totalModelPrice,
      insuranceId: selectedInsuranceConfig.id,
      insurancePackageId: selectedInsuranceConfig.packageId,
      insuranceDeductible: selectedInsuranceConfig.deductible,
      insuranceWindshieldLimit: selectedInsuranceConfig.windshieldLimit,
      claimFreeMonths: selectedInsuranceConfig.claimFreeMonths || undefined,
      clientAge: selectedInsuranceConfig.clientAge || undefined,
      params: items.map((item) => {
        return {
          paymentPeriod: item.month || undefined,
          advancePayment: item.advancedPayment != undefined ? Math.floor(item.advancedPayment * 100) : undefined,
          totalKmPerYear: item.totalKmPerYear || undefined,
        };
      }),
    };

    const result = await gqlCalculateFinances({
      variables: {
        input: inputVars,
      },
    });

    if (!result.data) {
      return null;
    }

    // if is credit calculation, the resulting price is a bit different
    if (props.enableCreditOptions && props.financeGroup.type === CreditTypeEnum.Crn) {
      // we need to call calculation once more, this time without VAT value (if is CRN) to get the correct INSURANCE final price
      inputVars.totalModelPrice = Math.round(inputVars.totalModelPrice / (1.0 + environment.VATValue));
      const insuranceResult = await gqlCalculateFinances({
        variables: {
          input: inputVars,
        },
      });

      if (!insuranceResult.data) {
        return null;
      }

      result.data.calculateVehicleFinance.calculation.calculationResults.forEach((calcResult, index) => {
        const correspondingInsuranceResult =
          insuranceResult.data!.calculateVehicleFinance.calculation.calculationResults.find(
            (insResult) =>
              insResult.paymentPeriodParam.usedValue === calcResult.paymentPeriodParam.usedValue &&
              insResult.advancePaymentParam.usedValue === calcResult.advancePaymentParam.usedValue,
          );
        if (correspondingInsuranceResult) {
          // recalculate the monthly instalment by removing old insurance value and adding the new one
          calcResult.monthlyInstalment =
            calcResult.monthlyInstalment -
            calcResult.monthlyInstalmentOnInsuranceOnly +
            correspondingInsuranceResult.monthlyInstalmentOnInsuranceOnly;
          calcResult.monthlyInstalmentOnInsuranceOnly = correspondingInsuranceResult.monthlyInstalmentOnInsuranceOnly;
        }
      });

      // now adjust the insurance payment on each calculation result
    }

    return result.data.calculateVehicleFinance.calculation.calculationResults;
  };

  const handleCreditGroupsCalculation = async () => {
    if (
      !selectedInsuranceConfig ||
      !selectedInsuranceConfig.id ||
      !selectedInsuranceConfig.packageId ||
      !selectedInsuranceConfig.deductible ||
      !selectedInsuranceConfig.windshieldLimit ||
      (enableCreditOptions && (!selectedInsuranceConfig.clientAge || !selectedInsuranceConfig.claimFreeMonths))
    ) {
      return;
    }

    const calculations: Array<CalculationResult> | null = await calculateFinances(props.financeGroup.items);
    if (!calculations) {
      return;
    }
    // Guard against null/zero quotes: a non-positive or non-finite payment is never a valid
    // result, so surface an error and do not apply it (defence in depth on top of the BE guard).
    if (calculations.some((result) => !isValidCalculatedPayment(result.monthlyInstalment))) {
      dispatch(setSnackbar(true, SnackbarStates.ERROR, t('userError.financeCalculation.invalidCalculationResult')));
      return;
    }
    setRepeatedCalculation(true);
    const groupItems = calculations.map((result: CalculationResult) => props.constructItemDto(result));
    const input = constructConfigObject(
      true,
      calculationDiscount,
      totalModelPrice,
      selectedInsuranceConfig,
      selectedProductId,
    );
    const calculationParams = CalculationConfigDto.convertFromCalculationInput(input);
    // @ts-ignore
    props.handleUpdate([...groupItems], calculationParams);
  };

  const reset = () => {
    initConfig();
  };

  const constructConfigObject = (
    automaticCalculation: boolean,
    discount: number,
    modelPrice: number,
    insuranceConfig?: Readonly<InsuranceInput>,
    productId?: string,
  ) => {
    return {
      productId: productId,
      id: insuranceConfig?.id,
      packageId: insuranceConfig?.packageId,
      deductible: insuranceConfig?.deductible,
      windshieldLimit: insuranceConfig?.windshieldLimit,
      automaticCalculation: automaticCalculation,
      carPriceDiscount: discount,
      totalModelPrice: modelPrice,
      regDateFirst: props.cardContext.regDateFirst || undefined,
      clientAge: insuranceConfig?.clientAge,
      claimFreeMonths: insuranceConfig?.claimFreeMonths,
      contraAccount:
        props.financeGroup.type === LeasingTypeEnum.ContraAccountCrn ||
        props.financeGroup.type === LeasingTypeEnum.ContraAccountPhysicalPerson,
    };
  };

  return (
    <Grid
      container
      direction={'column'}
      sx={{ padding: '16px 8px' }}
    >
      <Grid
        container
        justifyContent={'flex-start'}
        alignItems={'center'}
        sx={{ paddingBottom: '8px' }}
      >
        <Grid item>
          <FormControl
            className={classes.formControl}
            required
            error={!Boolean(props.financeGroupCalculationParams?.productId)}
          >
            <InputLabel id='product-select-label'>{t('fieldName.product')}</InputLabel>
            <Select
              labelId='product-select-label'
              id='product-select'
              value={props.financeGroupCalculationParams?.productId || ''}
              onChange={(event) => handleProductChange(event.target.value)}
              input={<Input />}
              MenuProps={MenuProps}
            >
              {financeProducts?.getFinanceProducts.map((product) => (
                <MenuItem
                  key={product.id}
                  value={product.id}
                >
                  {product.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      {currentDisclaimer && (
        <Grid
          item
          sx={{ paddingBottom: '1rem' }}
        >
          Disclaimer: {currentDisclaimer}
        </Grid>
      )}
      <Grid
        container
        justifyContent={'flex-start'}
        alignItems={'center'}
        sx={{ paddingBottom: '8px' }}
      >
        <InsuranceConfigOptions
          inputValues={props.insuranceConfigOptions}
          filteredValues={selectedInsuranceConfig}
          setFilteredValues={handleInsuranceConfigUpdate}
          enableCreditOptions={enableCreditOptions}
        />
      </Grid>
      <Grid
        container
        justifyContent={'flex-start'}
        alignItems={'center'}
        sx={{ padding: '8px' }}
      >
        <Grid item>
          <FormControl>
            <TextField
              variant={'outlined'}
              type={'number'}
              label={t('fieldName.calculationFinalPrice')}
              disabled
              value={totalModelPrice - calculationDiscount}
            />
          </FormControl>
        </Grid>
        <Grid item>
          <FormControl>
            <TextField
              variant={'outlined'}
              type={'number'}
              label={t('fieldName.insuranceDiscount')}
              disabled={loadingCalculationInfo}
              value={calculationDiscount || ''}
              defaultValue={0}
              onChange={(event) => updatePrice(parseInt(event.target.value))}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid
        container
        justifyContent={'flex-start'}
        alignItems={'center'}
        sx={{ padding: '8px' }}
      >
        <Grid item>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  value={repeatedCalculation}
                  disabled={loadingCalculationInfo}
                  onChange={(event, checked) => setRepeatedCalculation(checked)}
                />
              }
              label={t('fieldName.recalculationFlag')}
            />
          </FormGroup>
        </Grid>
      </Grid>
      <Grid
        container
        justifyContent={'flex-end'}
        alignItems={'center'}
      >
        <Grid item>
          <Button
            variant={'contained'}
            disabled={!inputIsValid() || loadingCalculationInfo}
            onClick={handleCreditGroupsCalculation}
          >
            {t('fieldName.calculatePayments')}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
}
